import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { slideToggle } from './../../composables/slideToggle.js';
import authService from '../../services/authService.js';
import notificationService from '../../services/notificationService.js';
import SearchDropdown from '../ui/SearchDropdown.jsx';

function Header() {
	const navigate = useNavigate();
	const [currentUser, setCurrentUser] = useState(null);
	const [notifications, setNotifications] = useState([]);
	const [unreadCount, setUnreadCount] = useState(0);
	const [isSearchOpen, setIsSearchOpen] = useState(false);
	const searchRef = useRef(null);
	
	useEffect(() => {
		// Get current user info
		const user = authService.getCurrentUser();
		setCurrentUser(user);
		
		// Initialize notifications
		setNotifications(notificationService.getRecent());
		setUnreadCount(notificationService.getUnreadCount());
		
		// Subscribe to notification updates
		const unsubscribe = notificationService.subscribe((updatedNotifications) => {
			setNotifications(notificationService.getRecent());
			setUnreadCount(notificationService.getUnreadCount());
		});
		
		// Cleanup subscription on unmount
		return unsubscribe;
	}, []);
	
	// Close search when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (searchRef.current && !searchRef.current.contains(event.target)) {
				setIsSearchOpen(false);
			}
		};
		
		if (isSearchOpen) {
			document.addEventListener('mousedown', handleClickOutside);
			return () => document.removeEventListener('mousedown', handleClickOutside);
		}
	}, [isSearchOpen]);
	
	const toggleAppSidebarDesktop = () => {
		var elm = document.querySelector('.app');
		if (elm) {
			if (!(elm.classList.contains('app-with-top-nav') && elm.classList.contains('app-without-sidebar'))) {
				elm.classList.toggle('app-sidebar-collapsed');
				
				setTimeout(() => {
					window.dispatchEvent(new Event('resize'));
				}, 150);
			}
		}
	}
	
	const toggleAppSidebarMobile = () => {
		var elm = document.querySelector('.app');
		if (elm) {
			if (!(elm.classList.contains('app-with-top-nav') && elm.classList.contains('app-without-sidebar'))) {
				elm.classList.toggle('app-sidebar-mobile-toggled');
			} else {
				slideToggle(document.querySelector('.app-top-nav'));
			}
		}
	}
	
	const toggleAppHeaderSearch = () => {
		setIsSearchOpen(!isSearchOpen);
	}
	
	const closeSearch = () => {
		setIsSearchOpen(false);
	}
	
	const handleLogout = async () => {
		try {
			await authService.logout();
			navigate('/pages/login');
		} catch (error) {
			console.error('Logout failed:', error);
			// Force logout even if API call fails
			navigate('/pages/login');
		}
	};
	
	const getUserDisplayName = () => {
		if (!currentUser) return 'Guest';
		return currentUser.username || 'User';
	};
	
	const getUserRole = () => {
		if (!currentUser) return '';
		return currentUser.role ? currentUser.role.charAt(0).toUpperCase() + currentUser.role.slice(1) : '';
	};
	
	const handleNotificationClick = (notification) => {
		if (!notification.isRead) {
			notificationService.markAsRead(notification.id);
		}
	};
	
	const handleMarkAllAsRead = () => {
		notificationService.markAllAsRead();
	};
	
	const handleClearNotifications = () => {
		notificationService.clear();
	};

	return (
		<div id="header" className="app-header">
			<div className="desktop-toggler">
				<button type="button" className="menu-toggler" onClick={toggleAppSidebarDesktop}>
					<span className="bar"></span>
					<span className="bar"></span>
					<span className="bar"></span>
				</button>
			</div>
			
			<div className="mobile-toggler">
				<button type="button" className="menu-toggler" onClick={toggleAppSidebarMobile}>
					<span className="bar"></span>
					<span className="bar"></span>
					<span className="bar"></span>
				</button>
			</div>
			
			<div className="brand">
				<Link to="/" className="brand-logo">
					<span className="brand-img">
						<span className="brand-img-text text-theme">GP</span>
					</span>
					<span className="brand-text">Cyber Point ADMIN</span>
				</Link>
			</div>
			
			<div className="menu">
				<div className="menu-item dropdown position-relative" ref={searchRef}>
					<a href="#/" onClick={toggleAppHeaderSearch} className="menu-link">
						<div className="menu-icon">
							<i className={`bi ${isSearchOpen ? 'bi-x-lg' : 'bi-search'} nav-icon`}></i>
						</div>
					</a>
					<SearchDropdown 
						isOpen={isSearchOpen} 
						onClose={closeSearch}
						onToggle={toggleAppHeaderSearch}
					/>
				</div>
				<div className="menu-item dropdown dropdown-mobile-full">
					<a href="#/" data-bs-toggle="dropdown" data-bs-display="static" className="menu-link">
						<div className="menu-icon"><i className="bi bi-grid-3x3-gap nav-icon"></i></div>
					</a>
					<div className="dropdown-menu fade dropdown-menu-end w-300px text-center p-0 mt-1">
						<div className="row row-grid gx-0">
							<div className="col-4">
								<Link to="/email/inbox" className="dropdown-item text-decoration-none p-3 bg-none">
									<div className="position-relative">
										<i className="bi bi-circle-fill position-absolute text-theme top-0 mt-n2 me-n2 fs-6px d-block text-center w-100"></i>
										<i className="bi bi-envelope h2 opacity-5 d-block my-1"></i>
									</div>
									<div className="fw-500 fs-10px text-inverse">INBOX</div>
								</Link>
							</div>
							<div className="col-4">
								<Link to="/pos/customer-order" className="dropdown-item text-decoration-none p-3 bg-none">
									<div><i className="bi bi-hdd-network h2 opacity-5 d-block my-1"></i></div>
									<div className="fw-500 fs-10px text-inverse">POS SYSTEM</div>
								</Link>
							</div>
							<div className="col-4">
								<Link to="/calendar" className="dropdown-item text-decoration-none p-3 bg-none">
									<div><i className="bi bi-calendar4 h2 opacity-5 d-block my-1"></i></div>
									<div className="fw-500 fs-10px text-inverse">CALENDAR</div>
								</Link>
							</div>
						</div>
						<div className="row row-grid gx-0">
							<div className="col-4">
								<Link to="/helper" className="dropdown-item text-decoration-none p-3 bg-none">
									<div><i className="bi bi-terminal h2 opacity-5 d-block my-1"></i></div>
									<div className="fw-500 fs-10px text-inverse">HELPER</div>
								</Link>
							</div>
							<div className="col-4">
                                <Link to="/admin/settings" className="dropdown-item text-decoration-none p-3 bg-none">
                                    <div className="position-relative">
                                        <i className="bi bi-circle-fill position-absolute text-theme top-0 mt-n2 me-n2 fs-6px d-block text-center w-100"></i>
                                        <i className="bi bi-sliders h2 opacity-5 d-block my-1"></i>
                                    </div>
                                    <div className="fw-500 fs-10px text-inverse">SETTINGS</div>
                                </Link>
                            </div>
							<div className="col-4">
								<Link to="/widgets" className="dropdown-item text-decoration-none p-3 bg-none">
									<div><i className="bi bi-collection-play h2 opacity-5 d-block my-1"></i></div>
									<div className="fw-500 fs-10px text-inverse">WIDGETS</div>
								</Link>
							</div>
						</div>
					</div>
				</div>
				<div className="menu-item dropdown dropdown-mobile-full">
					<a href="#/" data-bs-toggle="dropdown" data-bs-display="static" className="menu-link">
						<div className="menu-icon"><i className="bi bi-bell nav-icon"></i></div>
						{unreadCount > 0 && (
							<div className="menu-badge bg-theme">{unreadCount > 99 ? '99+' : unreadCount}</div>
						)}
					</a>
					<div className="dropdown-menu dropdown-menu-end mt-1 w-350px fs-11px pt-1">
						<div className="d-flex align-items-center justify-content-between px-3 py-2">
							<h6 className="dropdown-header fs-10px mb-0">NOTIFICATIONS</h6>
							{notifications.length > 0 && (
								<div className="d-flex gap-2">
									{unreadCount > 0 && (
										<button 
											type="button" 
											className="btn btn-sm btn-outline-primary fs-10px py-1 px-2"
											onClick={handleMarkAllAsRead}
										>
											Mark All Read
										</button>
									)}
									<button 
										type="button" 
										className="btn btn-sm btn-outline-secondary fs-10px py-1 px-2"
										onClick={handleClearNotifications}
									>
										Clear
									</button>
								</div>
							)}
						</div>
						<div className="dropdown-divider mt-1"></div>
						<div className="notification-list" style={{maxHeight: '300px', overflowY: 'auto'}}>
							{notifications.length > 0 ? (notifications.map((notification) => (
								<a 
									href="#/" 
									key={notification.id} 
									className={`d-flex align-items-center py-2 px-3 dropdown-item text-wrap fw-semibold position-relative ${
										!notification.isRead ? 'bg-light bg-opacity-50' : ''
									}`}
									onClick={() => handleNotificationClick(notification)}
								>
									{!notification.isRead && (
										<div className="position-absolute top-50 start-0 translate-middle-y ms-1">
											<div className="bg-primary rounded-circle" style={{width: '6px', height: '6px'}}></div>
										</div>
									)}
									<div className="fs-18px me-3" style={{marginLeft: !notification.isRead ? '12px' : '0'}}>
										<i className={notificationService.getIconClass(notification.type)}></i>
									</div>
									<div className="flex-1">
										<div className="mb-1 text-inverse" style={{fontSize: '12px', lineHeight: '1.3'}}>
											{notification.message}
										</div>
										<div className="small text-inverse text-opacity-50" style={{fontSize: '10px'}}>
											{notificationService.formatTime(notification.timestamp)}
										</div>
									</div>
								</a>
							))) : (
								<div className="p-3 text-center text-muted">
									<i className="bi bi-bell-slash fs-24px mb-2 d-block"></i>
									No notifications yet
								</div>
							)}
						</div>
					</div>
				</div>
				<div className="menu-item dropdown dropdown-mobile-full">
					<a href="#/" data-bs-toggle="dropdown" data-bs-display="static" className="menu-link">
						<div className="menu-img online">
							<div className="d-flex align-items-center justify-content-center w-100 h-100 bg-inverse bg-opacity-25 text-inverse text-opacity-50 rounded-circle overflow-hidden">
								<i className="bi bi-person-fill fs-32px mb-n3"></i>
							</div>
						</div>
						<div className="menu-text d-sm-block d-none w-170px">
							<div className="fw-bold">{getUserDisplayName()}</div>
							{getUserRole() && (
								<div className="text-inverse text-opacity-50 fs-10px">{getUserRole()}</div>
							)}
						</div>
					</a>
					<div className="dropdown-menu dropdown-menu-end me-lg-3 fs-11px mt-1">
						<div className="dropdown-header">
							<div className="fw-bold">{getUserDisplayName()}</div>
							{getUserRole() && (
								<div className="text-muted">{getUserRole()} Access</div>
							)}
						</div>
						<div className="dropdown-divider"></div>
						<Link to="/profile" className="dropdown-item d-flex align-items-center">PROFILE <i className="bi bi-person-circle ms-auto text-theme fs-16px my-n1"></i></Link>
						<Link to="/settings" className="dropdown-item d-flex align-items-center">SETTINGS <i className="bi bi-gear ms-auto text-theme fs-16px my-n1"></i></Link>
						{authService.hasRole('admin') && (
						<>
							<Link to="/admin/users" className="dropdown-item d-flex align-items-center"><i className="bi bi-people me-2"></i>USER MANAGEMENT <i className="bi bi-people ms-auto text-theme fs-16px my-n1"></i></Link>
							<Link to="/admin/statistics" className="dropdown-item d-flex align-items-center"><i className="bi bi-bar-chart me-2"></i>STATISTICS <i className="bi bi-bar-chart ms-auto text-theme fs-16px my-n1"></i></Link>
						</>
					)}
						<div className="dropdown-divider"></div>
						<button 
							onClick={handleLogout} 
							className="dropdown-item d-flex align-items-center border-0 bg-transparent w-100 text-start"
							style={{ cursor: 'pointer' }}
						>
							LOGOUT <i className="bi bi-box-arrow-right ms-auto text-theme fs-16px my-n1"></i>
						</button>
					</div>
				</div>
			</div>
		</div>
	)
}

export default Header;
